import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type DeliveryZone,
  type DiscardReason,
  type LeadStatus,
  type LinkCandidate,
  type ReferralDetail,
  type ReferralFilters,
  type ReferralPage,
  type ReferralSummary,
  type UnlinkReason,
} from "../../domain/entities/referral-backoffice";
import {
  GetReferralError,
  ListZonesError,
  LinkCandidatesError,
  LinkReferralError,
  ListReferralsError,
  ReferralSummaryError,
  UnlinkReferralError,
  UpdateReferralStatusError,
} from "../../domain/errors/errors";
import {
  type ReferralsBackofficeRepository,
  type SummaryParams,
} from "../../domain/repos/referrals-backoffice-repo";
import {
  type LinkCandidatesResponse,
  type ReferralDetailResponse,
  type ReferralListResponse,
  type ReferralSummaryResponse,
  type ZonesResponse,
} from "../../infrastructure/responses/referrals-backoffice-responses";

/**
 * Builds a query string from defined values only. An `undefined` filter must
 * not reach the wire as the string "undefined" — the backend validates the
 * enums and would 400 on it.
 */
const queryOf = (
  params: Record<string, string | number | undefined>,
): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.append(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
};

export class ReferralsBackofficeRepo implements ReferralsBackofficeRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  /** The endpoint returns only delivery zones, already the assignable set. */
  async listZones(): Promise<DeliveryZone[]> {
    try {
      const response = await this.apiClient.get<ZonesResponse>(
        "v1/zones?country_code=gt",
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListZonesError(message, apiError);
    }
  }

  async list(filters: ReferralFilters): Promise<ReferralPage> {
    try {
      const query = queryOf({
        supervisor_id: filters.supervisorId,
        ambassador_id: filters.ambassadorId,
        from: filters.from,
        to: filters.to,
        status: filters.status,
        link_status: filters.linkStatus,
        q: filters.q,
        page_size: filters.pageSize,
        page_token: filters.pageToken,
      });
      const response = await this.apiClient.get<ReferralListResponse>(
        `v1/referrals${query}`,
      );
      return {
        items: response.data?.items ?? [],
        nextToken: response.data?.nextToken,
      };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListReferralsError(message, apiError);
    }
  }

  async get(
    referralId: string,
    supervisorId?: string,
  ): Promise<ReferralDetail> {
    try {
      const query = queryOf({ supervisor_id: supervisorId });
      const response = await this.apiClient.get<ReferralDetailResponse>(
        `v1/referrals/${referralId}${query}`,
      );
      return this.detailOf(response);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetReferralError(message, apiError);
    }
  }

  async summary(params: SummaryParams): Promise<ReferralSummary> {
    try {
      const query = queryOf({
        group_by: params.groupBy,
        from: params.from,
        to: params.to,
        supervisor_id: params.supervisorId,
        zone_id: params.zoneId,
        series: params.series,
        series_periods: params.seriesPeriods,
      });
      const response = await this.apiClient.get<ReferralSummaryResponse>(
        `v1/referrals/summary${query}`,
      );
      const summary = response.data;
      if (!summary) throw new ReferralSummaryError("Empty summary response.");
      return summary;
    } catch (error) {
      if (error instanceof ReferralSummaryError) throw error;
      const { message, apiError } = extractApiError(error);
      throw new ReferralSummaryError(message, apiError);
    }
  }

  async linkCandidates(
    referralId: string,
    q?: string,
  ): Promise<LinkCandidate[]> {
    try {
      const query = queryOf({ q });
      const response = await this.apiClient.get<LinkCandidatesResponse>(
        `v1/referrals/${referralId}/link-candidates${query}`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new LinkCandidatesError(message, apiError);
    }
  }

  async link(
    referralId: string,
    requestId: string,
  ): Promise<ReferralDetail> {
    try {
      const response = await this.apiClient.patch<
        { requestId: string },
        ReferralDetailResponse
      >(`v1/referrals/${referralId}/link`, { requestId });
      return this.detailOf(response);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new LinkReferralError(message, apiError);
    }
  }

  async unlink(
    referralId: string,
    reason: UnlinkReason,
  ): Promise<ReferralDetail> {
    try {
      // DELETE with a body: undoing an attribution is audited, so the reason
      // travels with the call rather than on the query string.
      const response = await this.apiClient.delete<
        ReferralDetailResponse,
        { reason: UnlinkReason }
      >(`v1/referrals/${referralId}/link`, { reason });
      return this.detailOf(response);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UnlinkReferralError(message, apiError);
    }
  }

  async updateStatus(
    referralId: string,
    status: Extract<LeadStatus, "contacted" | "discarded">,
    reason?: DiscardReason,
  ): Promise<ReferralDetail> {
    try {
      const response = await this.apiClient.patch<
        { status: string; reason?: DiscardReason },
        ReferralDetailResponse
      >(`v1/referrals/${referralId}/status`, { status, reason });
      return this.detailOf(response);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UpdateReferralStatusError(message, apiError);
    }
  }

  /** Every write answers with the full detail, so the UI refreshes from it. */
  private detailOf(response: ReferralDetailResponse): ReferralDetail {
    const detail = response.data;
    if (!detail) throw new GetReferralError("Empty referral response.");
    return detail;
  }
}
